import PropTypes from 'prop-types';
import { Icon, ButtonTooltip, Tooltip2, useOpenClose } from '@evertrue/et-components';
import PhilanthropicIndicatorsModal from './philanthropic-indicators-modal';

const DISPLAY_LIMIT = 6;

/**
 * Formats a date string to "Month Day, Year" format.
 */
const formatLastModifiedDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Gets the most recent updated_at date from indicators and interests.
 */
const getLastModifiedDate = (indicators = [], interests = []) => {
  const allDates = [...indicators, ...interests]
    .map((item) => item.updated_at)
    .filter(Boolean)
    .map((d) => new Date(d).getTime());

  if (allDates.length === 0) return null;
  const mostRecent = new Date(Math.max(...allDates));
  return formatLastModifiedDate(mostRecent);
};

/**
 * Displays philanthropic indicators including leadership/involvement and interests as tag chips.
 */
const PhilanthropicIndicatorsCard = ({ wealthIndicators = [], philInterests = [], contactAttributes, onDelete }) => {
  const [isModalOpen, openModal, closeModal] = useOpenClose();
  const filteredIndicators = wealthIndicators.filter(({ rejected }) => !rejected);
  const filteredInterests = philInterests.filter(({ rejected }) => !rejected);

  const displayedIndicators = filteredIndicators.slice(0, DISPLAY_LIMIT);
  const remainingIndicatorsCount = filteredIndicators.length - DISPLAY_LIMIT;

  const displayedInterests = filteredInterests.slice(0, DISPLAY_LIMIT);
  const remainingInterestsCount = filteredInterests.length - DISPLAY_LIMIT;

  const lastModifiedDate = getLastModifiedDate(wealthIndicators, philInterests);

  return (
    <div className="philanthropic-indicators-card">
      <div className="philanthropic-indicators-card--header">
        <div className="philanthropic-indicators-card--header-left">
          <Icon icon="thumbs-up" />
          {lastModifiedDate ? (
            <Tooltip2 trigger={<span className="philanthropic-indicators-card--title">Philanthropic Indicators</span>}>
              Data was last added to this section on {lastModifiedDate}
            </Tooltip2>
          ) : (
            <span className="philanthropic-indicators-card--title">Philanthropic Indicators</span>
          )}
        </div>
        {(wealthIndicators.length > 0 || philInterests.length > 0) && (
          <button
            type="button"
            className="philanthropic-indicators-card--edit-btn"
            onClick={openModal}
            aria-label="Edit philanthropic indicators"
          >
            <Icon icon="edit" title="Edit philanthropic indicators" />
          </button>
        )}
      </div>

      <div className="philanthropic-indicators-card--content">
        <div className="philanthropic-indicators-card--section">
          <div className="philanthropic-indicators-card--section-header">
            <span>Leadership and Involvement</span>
            <ButtonTooltip
              trigger={<Icon icon="info-circle" iconKit="gt2" style={{ color: '#b7b7b7' }} />}
              contentWidth={280}
              disableArrow
            >
              Leadership and Involvement highlights affiliations or roles associated with a constituent, such as
              foundation involvement or nonprofit leadership, that may indicate capacity or engagement.
            </ButtonTooltip>
          </div>
          <div className="philanthropic-indicators-card--tags">
            {displayedIndicators.length > 0 ? (
              displayedIndicators.map((indicator) => (
                <span key={indicator.id} className="philanthropic-indicators-card--tag">
                  {indicator.type}
                </span>
              ))
            ) : (
              <span className="philanthropic-indicators-card--empty-text">
                No enriched Leadership and Involvement data available for this constituent
              </span>
            )}
          </div>
          {remainingIndicatorsCount > 0 && (
            <button type="button" className="philanthropic-indicators-card--more-btn" onClick={openModal}>
              (+{remainingIndicatorsCount} More)
            </button>
          )}
        </div>

        <div className="philanthropic-indicators-card--section">
          <div className="philanthropic-indicators-card--section-header">
            <span>Interests</span>
            <ButtonTooltip
              trigger={<Icon icon="info-circle" iconKit="gt2" style={{ color: '#b7b7b7' }} />}
              contentWidth={280}
              disableArrow
            >
              Interests highlight causes and types of organizations a constituent may prioritize in their giving. These
              can help tailor outreach and understand what issues may resonate most.
            </ButtonTooltip>
          </div>
          <div className="philanthropic-indicators-card--tags">
            {displayedInterests.length > 0 ? (
              displayedInterests.map((interest) => (
                <span key={interest.id} className="philanthropic-indicators-card--tag">
                  {interest.interest_name}
                </span>
              ))
            ) : (
              <span className="philanthropic-indicators-card--empty-text">
                No enriched Interest data available for this constituent
              </span>
            )}
          </div>
          {remainingInterestsCount > 0 && (
            <button type="button" className="philanthropic-indicators-card--more-btn" onClick={openModal}>
              (+{remainingInterestsCount} More)
            </button>
          )}
        </div>
      </div>

      {isModalOpen && (
        <PhilanthropicIndicatorsModal
          isOpen={isModalOpen}
          closeModal={closeModal}
          wealthIndicators={wealthIndicators}
          philInterests={philInterests}
          contactAttributes={contactAttributes}
          onDelete={onDelete}
        />
      )}
    </div>
  );
};

PhilanthropicIndicatorsCard.propTypes = {
  wealthIndicators: PropTypes.array,
  philInterests: PropTypes.array,
  contactAttributes: PropTypes.object,
  onDelete: PropTypes.func,
};

export default PhilanthropicIndicatorsCard;
